import { computed, Injectable, signal } from '@angular/core';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from '@/app.config';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import { DateTime } from 'luxon';

import {
    SearchConditions,
    FilterType,
    CommonList,
    StreamSortType,
    FolderWidgetInfo,
    FolderDetail,
    Card,
    PreviewCard,
    Question,
    ContentHistoryInfo,
    ModuleSection,
    ModuleInfo,
    StreamData,
    RootStreamInfo,
    EditCardResponse
} from './content.model';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class ContentService {
    public searchConditions: BehaviorSubject<SearchConditions> =
        new BehaviorSubject<SearchConditions>(new SearchConditions());
    private commonLists: CommonList = {} as CommonList;
    private roleCountry$: BehaviorSubject<any> = new BehaviorSubject<boolean>(null);
    private skillObjective$: BehaviorSubject<any> = new BehaviorSubject<boolean>(null);
    public localeDir$: BehaviorSubject<string> = new BehaviorSubject<string>('ltr');
    // private folderList$ = new BehaviorSubject<FolderWidgetInfo[]>(null);
    private folderList = <FolderWidgetInfo[]>[];
    private _canEdit = new BehaviorSubject<boolean>(true);
    canEdit$ = this._canEdit.asObservable();
    private readonly CAN_EDIT_KEY = 'canEdit';
    private readonly SECTION_ID_KEY = 'sectionId';
    private readonly STREAM_DETAILS_KEY = 'streamDetail';
    private readonly hasModified = signal(false);
    public hasModified$ = computed(() => this.hasModified());

    constructor(
        private http: HttpClient,
        private logger: NGXLogger,
        private config: AppConfig,
        private sanitizer: DomSanitizer
    ) {
        const folderDetail = JSON.parse(sessionStorage.getItem('folderDetail'));
        this.setSelectedFolder(folderDetail);
        const canEdit = sessionStorage.getItem(this.CAN_EDIT_KEY);
        if (canEdit !== null) {
            this._canEdit.next(canEdit === 'true');
        }
    }

    clear() {
        this.logger.info('User Logout: Clearing Common Content Service Data');
        this.commonLists = {} as CommonList;
        this.searchConditions.next(new SearchConditions());
    }

    setCanEdit(value: boolean) {
        this._canEdit.next(value);
        sessionStorage.setItem(this.CAN_EDIT_KEY, JSON.stringify(value));
    }

    // Functions related to saving common data to session
    commonData(type) {
        const currValue = this.commonLists[type];
        if (currValue) {
            return of(currValue);
        }
        if (sessionStorage.getItem(type)) {
            this.commonLists[type] = JSON.parse(sessionStorage.getItem(type));
            return of(this.commonLists[type]);
        }
        return this.getValueFromApi(type);
    }

    setCommonData(type, value) {
        this.commonLists[type] = value;
        sessionStorage.setItem(type, JSON.stringify(value));
    }

    // Functions related for Search Filters
    setSearchFilters() {
        // const currValue = this.searchConditions.value;
        // if (!currValue || !Object.keys(currValue).length) {
        this.searchConditions.next(
            new SearchConditions(JSON.parse(sessionStorage.getItem('searchConditions')))
        );
        // }
    }

    updateSearchKey(newKey: string) {
        const newConditions = this.searchConditions.value;
        if (newKey !== newConditions.searchKey) {
            newConditions.searchKey = newKey;
            this.searchConditions.next(newConditions);
            sessionStorage.setItem('searchConditions', JSON.stringify(newConditions));
        }
    }

    updateFilters(
        filters: { [index: string]: Array<string> },
        resetSearchKey = false,
        resetSortBy = false
    ) {
        const newConditions = this.searchConditions.value;
        Object.keys(filters).forEach(key => {
            newConditions.filters[key] = filters[key];
        });
        if (resetSearchKey) {
            newConditions.searchKey = null;
        }
        if (resetSortBy) {
            newConditions.sortBy = 'relevance';
        }
        this.searchConditions.next(newConditions);
        sessionStorage.setItem('searchConditions', JSON.stringify(newConditions));
    }

    updateFilter(filterType: FilterType, newValue: Array<string>) {
        if (newValue) {
            const newConditions = this.searchConditions.value;
            newConditions.filters[filterType] = newValue;
            this.searchConditions.next(newConditions);
            sessionStorage.setItem('searchConditions', JSON.stringify(newConditions));
        }
    }

    updateSortBy(type: StreamSortType) {
        if (type) {
            const newConditions = this.searchConditions.value;
            if (newConditions.sortBy !== type) {
                newConditions.sortBy = type;
                this.searchConditions.next(newConditions);
            }
            sessionStorage.setItem('searchConditions', JSON.stringify(newConditions));
        }
    }

    getValueFromApi(type) {
        switch (type) {
            case 'created_by':
                return this.getFilterList(type);
            case 'streaming_to':
                return this.getFilterList(type);
            default:
                return from([]);
        }
    }

    getFilterList(type) {
        const listObservable = this.http
            .post<any>(`${this.config.apiUrl}/search/get_drop_down_for_content/`, {
                drop_down_type: type.toUpperCase(),
                content_filter: {
                    lang_code: [],
                    status: [],
                    streaming_to: [],
                    created_by: [],
                    skills: [],
                    type: []
                }
            })
            .pipe(
                map(response => {
                    this.setCommonData(type, response);
                    return response;
                })
            );
        return listObservable;
    }

    redirectToSection(streamDetail: RootStreamInfo, langCode) {
        sessionStorage.setItem('persistIndex', JSON.stringify(false));
        sessionStorage.setItem('streamDetail', JSON.stringify(streamDetail));
        // need to remove sessionStorage once section page is moved to angular
        this.folderList = JSON.parse(sessionStorage.getItem('folderList'));
        const folderInfo = this.folderList.find(
            folder => folder.id === streamDetail.streamInfo.folders[0]
        );
        const folderDetail = {
            id: folderInfo.id,
            isApprover: streamDetail.streamInfo.isApprover,
            objId: null,
            objTitle: null,
            title: folderInfo.name,
            type: 'folder'
        };
        sessionStorage.setItem('folderDetail', JSON.stringify(folderDetail));
        this.setSelectedFolder(folderDetail);
        const moduleDetail = JSON.stringify({
            id: streamDetail.streamInfo.id,
            isEditor: streamDetail.streamInfo.can_edit,
            title: streamDetail.streamInfo.title,
            langCode,
            langLabel: streamDetail.streamInfo.languageLabel
        });
        sessionStorage.setItem('moduleDetail', moduleDetail);
    }

    isApprover(folderDetail: FolderDetail): boolean {
        let isApprover = false;
        if (this.folderList) {
            const folderList = this.folderList;
            const checkForFolderApprover = folderList.findIndex(function (n, i) {
                return n.id == folderDetail.id;
            });
            if (checkForFolderApprover > -1) {
                isApprover = folderList[checkForFolderApprover].isApprover;
            }
        } else {
            isApprover = folderDetail.isApprover;
        }

        return isApprover;
    }

    getTitle(folderDetail: FolderDetail): string {
        if (this.folderList) {
            const folder = this.folderList.find(file => file.id == folderDetail.id);
            if (folder) {
                return folder.name;
            }
        }

        return null;
    }

    historyInfoSubject = new BehaviorSubject<ContentHistoryInfo[]>(null);
    showOverlaySubject = new BehaviorSubject<boolean>(false);

    get showOverlay() {
        return this.showOverlaySubject.asObservable();
    }

    refetchSubject = new BehaviorSubject(null);

    get refetch() {
        return this.refetchSubject.asObservable();
    }

    getSectionData(section: ModuleSection) {
        if (!section) return of();
        return this.http.post<any>(`${this.config.apiUrl}/section/get_sections_data/`, {
            section_id: section.section_id
        });
    }

    getPreviewCard(card: Card) {
        if (!card) return of();
        return this.http.post<PreviewCard>(`${this.config.apiUrl}/section/get_preview_card/`, {
            card_id: card.id
        });
    }

    getPreviewQuestionCard(questionId?: number) {
        if (!questionId) return of();
        return this.http.post<PreviewCard>(`${this.config.apiUrl}/section/get_preview_question/`, {
            question_id: questionId ?? ''
        });
    }

    getHistory(version: number, moduleDetail) {
        this.http
            .post<ContentHistoryInfo[]>(`${this.config.apiUrl}/auditlog/get_history/`, {
                module_id: moduleDetail.id,
                version: version
            })
            .subscribe({
                next: res => {
                    this.historyInfoSubject.next(res);
                }
            });
    }

    moveCard(
        card: Card,
        sourceSection: ModuleSection,
        destinationSection: ModuleSection
    ): Observable<any> {
        return this.http.post(`${this.config.apiUrl}/card/drag_card_across_sections/`, {
            card_id: card.id,
            source_section: sourceSection.section_id,
            destination_section: destinationSection.section_id
        });
    }

    deleteSection(section: ModuleSection, moduleDetail) {
        return this.http.post(`${this.config.apiUrl}/section/delete_section/`, {
            section_id: section.section_id,
            module_id: moduleDetail.id
        });
    }

    deleteCard(card_id): Observable<any> {
        return this.http.post(`${this.config.apiUrl}/card/delete_card/`, {
            module_id: this.activeSection.value.module_id,
            section_id: this.activeSection.value.section_id,
            card_id: card_id
        });
    }

    removeCard(card_id: number, type = 'card') {
        type === 'card'
            ? this.setCards(this._cards.value?.filter(card => card.id != card_id))
            : this.setQuestions(this._questions.value?.filter(ques => ques.id != card_id));
    }

    deleteQuestion(question_id): Observable<any> {
        return this.http.post(`${this.config.apiUrl}/questions/delete_question/`, {
            module_id: this.activeSection.value.module_id,
            section_id: this.activeSection.value.section_id,
            question_id: question_id
        });
    }

    setSectionId(sectionId: number) {
        sessionStorage.setItem(this.SECTION_ID_KEY, JSON.stringify(sectionId));
    }

    isInitialized$ = new BehaviorSubject<boolean>(true);

    private _selectedFolder = new BehaviorSubject<any>(null);

    get selectedFolder$(): Observable<any> {
        return this._selectedFolder.asObservable();
    }

    setSelectedFolder(folderInfo) {
        this._selectedFolder.next(folderInfo);
    }

    private _isLoading = new BehaviorSubject<boolean>(true);

    get isLoading$(): Observable<boolean> {
        return this._isLoading.asObservable();
    }

    setIsLoading(loading: boolean) {
        this._isLoading.next(loading);
    }

    activeTabSubject = new BehaviorSubject<ModuleInfo>(null);

    get activeTab() {
        return this.activeTabSubject.asObservable().pipe(filter(res => !!res));
    }

    get activeStatus(): Observable<string> {
        return this.activeTab.pipe(map(res => res.state));
    }

    activeSection = new BehaviorSubject<ModuleSection>(null);

    get section() {
        return this.activeSection.asObservable().pipe(distinctUntilChanged());
    }

    activeCard = new BehaviorSubject<Card>(null);

    get card$() {
        return this.activeCard.asObservable();
    }

    activeQuestion = new BehaviorSubject<Question>(null);

    get question$() {
        return this.activeQuestion.asObservable();
    }

    _cards = new BehaviorSubject<Card[]>(null);
    get cards$() {
        return this._cards.asObservable();
    }
    setCards(cards: Card[]) {
        this._cards.next(cards);
    }

    _questions = new BehaviorSubject<Question[]>(null);
    get questions$() {
        return this._questions.asObservable();
    }
    setQuestions(questions: Question[]) {
        this._questions.next(questions);
    }

    _isRTL = new BehaviorSubject<boolean>(false);

    get isRTL$() {
        return this._isRTL.asObservable();
    }

    setIsRTL(direction: boolean) {
        return this._isRTL.next(direction);
    }

    _isReadOnly = this.activeTabSubject.pipe(
        map(res => (res?.state === 'EDITED' || res?.state === 'REJECTED' ? false : true ?? true))
    );
    get isReadOnly$() {
        return this._isReadOnly;
    }

    _resetCardIndices = new BehaviorSubject<boolean>(false);
    get resetCardIndices$() {
        return this._resetCardIndices;
    }
    setResetCardIndices(value: boolean) {
        this._resetCardIndices.next(value);
    }

    refresh() {
        this.setIsLoading(true);
        this.resetStreams();
        this.refetchSubject.next(null);
    }

    resetStreams() {
        this.isInitialized$.next(true);
        this.activeCard.next(null);
        this.activeTabSubject.next(null);
        this.activeQuestion.next(null);
        this._cards.next(null);
        this._questions.next(null);
    }

    reorderCards(id_list: number[], type: 'card' | 'question' | 'section') {
        return this.http.post(`${this.config.apiUrl}/card/reordercard/`, {
            id_list: id_list,
            module_id: this.activeSection.value.module_id,
            section_id: this.activeSection.value.section_id,
            type: type
        });
    }

    preparePayload(stream: StreamData, streamAction?: string, autoTranslate?: boolean) {
        stream.imageUrl =
            stream.imageUrl.indexOf('data:base64') >= 0
                ? stream.imageUrl
                : stream.imageUrl.indexOf('http') === -1
                ? stream.imageUrl
                : stream.imageUrl.replace(stream.baseUrl, '');
        const payload: any = {
            lang: { label: stream.languageLabel, value: stream.languageCode },
            imageUrl: stream.imageUrl,
            title: stream.title,
            desc: stream.desc,
            visibility: stream.visibility,
            numAssessmentQ: null,
            alertOnExpiry: stream.streamConfig?.alert_on_expiry,
            hasKnowledgeCheck: stream.module_config?.hasKnowledgeCheck,
            isReference: stream.isReference,
            kcConfig: {
                sendAssess: stream.module_config?.kcConfig?.sendAssess,
                timerType: stream.module_config?.kcConfig?.timerType,
                timerValue: stream.module_config?.kcConfig?.timerValue,
                numQues: stream.module_config?.kcConfig?.numQues
            },
            lockSection: stream.streamConfig?.lock_section,
            sectionConfig: {
                type: stream.streamConfig?.unlock_section_detail?.type,
                day: stream.streamConfig?.unlock_section_detail?.day,
                date: stream.streamConfig?.unlock_section_detail?.date
            },
            sectionEnabled: stream.streamConfig?.unlock_section,
            userAcknowlegement: stream.streamConfig?.user_acknowlegement,
            userFeedback: stream.streamConfig?.feedback,
            aiCreate: false
        };
        if (streamAction === 'create' || streamAction === 'AICreate') {
            payload.sectionTitle = stream.sectionTitle;
            payload.objectiveIds = stream.skillIdList;
            if (streamAction === 'AICreate') {
                payload.aiCreate = true;
                payload.fileName = stream.fileName;
                payload.configJson = stream.configJson;
            }
        } else if (streamAction === 'duplicate') {
            payload['autoTranslate'] = autoTranslate;
            payload.id = stream.id;
        } else {
            payload.id = stream.id;
        }
        return payload;
    }

    createStreamDetailsObj(stream, id, language, languageList, langCode, index) {
        let lastStreamedDate = Object.keys(stream.streamingInfo).length
            ? stream.streamingInfo.streamedDate
            : '01-Jan-1990'; // null
        lastStreamedDate = DateTime.fromFormat(lastStreamedDate, 'dd-MMM-yyyy').toFormat(
            'yyyy-MM-dd'
        );

        let lastModifiedDate = Object.keys(stream.approvedVersionInfo).length
            ? stream.approvedVersionInfo.lastUpdatedDate
            : stream.pendingVersionInfo.lastUpdatedDate;
        if (lastModifiedDate) {
            lastModifiedDate = DateTime.fromFormat(lastModifiedDate, 'dd-MMM-yyyy').toFormat(
                'yyyy-MM-dd'
            );
        }

        const streamLangList = [];
        languageList.forEach(n => {
            if (Object.keys(language).indexOf(n.value) > -1) {
                streamLangList.push(n);
            }
        });
        stream.isTranslating =
            'duplication_in_process' in stream.module_config
                ? stream.module_config.duplication_in_process
                : '';
        stream.isSynching =
            'syncing_in_progress' in stream.module_config
                ? stream.module_config.syncing_in_progress
                : false;
        stream.isAIGenerating =
            'ai_creation_in_progress' in stream.module_config
                ? stream.module_config.ai_creation_in_progress
                : false;
        stream.task_id = 'task_id' in stream.module_config ? stream.module_config.task_id : '';
        stream.isAi =
            'created_by_ai' in stream.module_config ? stream.module_config.created_by_ai : false;
        const obj = {
            rootModuleId: id,
            streamInfo: stream,
            existingLangList: streamLangList,
            streamedDate: lastStreamedDate,
            lastUpdatedDate: lastModifiedDate,
            index: index,
            langCode: langCode // need to remove once section is moved to angular
        };
        return obj;
    }

    getSessionStreamDetail() {
        return JSON.parse(sessionStorage.getItem(this.STREAM_DETAILS_KEY));
    }

    clearSessionStreamDetail() {
        sessionStorage.removeItem(this.STREAM_DETAILS_KEY);
    }

    // For content template

    getEditCard(cardId) {
        const formData = new FormData();
        formData.append('card_id', cardId);
        return this.http.post<EditCardResponse>(
            `${this.config.apiUrl}/card/get_edit_card/`,
            formData
        );
    }

    getQuestionCard(cardId) {
        const formData = new FormData();
        formData.append('question_id', cardId);
        return this.http.post<any>(
            `${this.config.apiUrl}/questions/get_question_detail/`,
            formData
        );
    }

    buildSafeHtml(preCard: PreviewCard, showOriginal = false, isRtl = false) {
        preCard.safe_html = this.sanitizer.bypassSecurityTrustHtml(
            showOriginal
                ? this.addCfUrlToImgSrc(
                      preCard.original_ck_html,
                      preCard.cf_url,
                      isRtl,
                      preCard.original_title
                  )
                : this.addCfUrlToImgSrc(preCard.ck_html, preCard.cf_url, isRtl, preCard.title)
        );
        return preCard;
    }

    updateHasModified(value: boolean) {
        this.hasModified.set(value);
    }

    addCfUrlToImgSrc(html: string, url: string, isRtl: boolean, title?: string): string {
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(html, 'text/html');
        const imgTag = htmlDoc.getElementsByTagName('img');
        if (imgTag.length) {
            for (let i = 0; i < imgTag.length; i++) {
                const img = imgTag[i];
                img.setAttribute('src', url + img.getAttribute('src'));
            }
        }

        const styleElement = htmlDoc.createElement('style');
        styleElement.innerHTML = `
            body {
                font-family: 'Open Sans', sans-serif;
                font-size: 13px;
            }

            .preview-card-title {
            font-size: 16px;
            font-weight: 600;
            word-wrap: break-word;
            text-align: left;
            margin: 6px 0;
            ${isRtl ? 'direction: rtl;' : ''}
            }`;
        htmlDoc.getElementsByTagName('head')[0].appendChild(styleElement);

        const linkElement = htmlDoc.createElement('link');
        linkElement.rel = 'stylesheet';
        linkElement.href = 'assets/stylesheets/bootstrap.min.css';

        htmlDoc.getElementsByTagName('head')[0].appendChild(linkElement);

        const bodyTag = htmlDoc.getElementsByTagName('body')[0];
        if (title) {
            const h5Element = htmlDoc.createElement('h5');
            h5Element.innerText = title;
            h5Element.setAttribute('class', 'preview-card-title');
            bodyTag.insertBefore(h5Element, bodyTag.firstChild);
        }

        return htmlDoc.documentElement.innerHTML;
    }
}
